import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo, useState, } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { dataverseScopes, dataverseUrl, loginScopes } from "./msal";
import { setTokenProvider } from "@/data";
const Ctx = createContext(null);
const MODE = (import.meta.env.VITE_BACKEND ?? "mock");
const VALID_ROLES = [
    "Finance.Approver",
    "Operations.Manager",
    "Project.Manager",
    "Admin",
];
const MOCK_USER = {
    id: "tim-user-id",
    bookableResourceId: "br-0001",
    name: "Tim Raines",
    email: "Tim@raines.com.au",
    jobTitle: "Business Owner",
    businessUnitId: "bu-root",
};
export function AuthProvider({ children }) {
    if (MODE === "dataverse")
        return _jsx(MsalAuthProvider, { children: children });
    return _jsx(MockAuthProvider, { children: children });
}
function MockAuthProvider({ children }) {
    const state = useMemo(() => ({
        user: MOCK_USER,
        roles: ["Admin"],
        isMock: true,
        getToken: async () => "mock-token",
    }), []);
    return _jsx(Ctx.Provider, { value: state, children: children });
}
function MsalAuthProvider({ children }) {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthed = useIsAuthenticated();
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);
    const [identity, setIdentity] = useState(null);
    // Kick off interactive sign-in if no account after MSAL has settled.
    useEffect(() => {
        if (inProgress !== "none")
            return;
        if (accounts.length === 0) {
            instance
                .loginRedirect({ scopes: loginScopes })
                .catch((e) => setError(String(e)));
        }
    }, [instance, accounts, inProgress]);
    // Once an account is present, register a token provider on the data layer
    // and resolve the signed-in user's Dataverse systemuser + bookableresource.
    // The Entra `oid` is NOT the bookableresource id — bookableresource is a
    // separate Dataverse record whose `_userid_value` points to systemuser.
    useEffect(() => {
        if (!isAuthed || accounts.length === 0)
            return;
        const account = accounts[0];
        instance.setActiveAccount(account);
        const getToken = async (scopes = dataverseScopes) => {
            try {
                const res = await instance.acquireTokenSilent({ scopes, account });
                return res.accessToken;
            }
            catch (e) {
                if (e instanceof InteractionRequiredAuthError) {
                    await instance.acquireTokenRedirect({ scopes });
                }
                throw e;
            }
        };
        setTokenProvider(getToken);
        let cancelled = false;
        void (async () => {
            try {
                const id = await resolveIdentity(getToken);
                if (cancelled)
                    return;
                setIdentity(id);
                setReady(true);
            }
            catch (e) {
                if (cancelled)
                    return;
                setError(e instanceof Error ? e.message : String(e));
            }
        })();
        return () => {
            cancelled = true;
            setTokenProvider(null);
        };
    }, [instance, accounts, isAuthed]);
    if (error) {
        return (_jsxs("div", { style: { padding: 24, fontFamily: "system-ui", color: "#b91c1c" }, children: [_jsx("h2", { children: "Sign-in error" }), _jsx("pre", { style: { whiteSpace: "pre-wrap" }, children: error })] }));
    }
    if (!ready || !identity) {
        return (_jsx("div", { style: { padding: 24, fontFamily: "system-ui", color: "#475569" }, children: "Signing you in\u2026" }));
    }
    const account = accounts[0];
    const user = accountToUser(account, identity);
    const roles = rolesFromAccount(account);
    const state = {
        user,
        roles,
        isMock: false,
        getToken: async (scopes) => {
            const res = await instance.acquireTokenSilent({
                scopes: scopes ?? dataverseScopes,
                account,
            });
            return res.accessToken;
        },
    };
    return _jsx(Ctx.Provider, { value: state, children: children });
}
async function resolveIdentity(getToken) {
    if (!dataverseUrl) {
        throw new Error("VITE_DATAVERSE_URL is not configured. Set it in your environment and rebuild.");
    }
    const token = await getToken();
    const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "OData-MaxVersion": "4.0",
        "OData-Version": "4.0",
    };
    const whoAmIRes = await fetch(`${dataverseUrl}/api/data/v9.2/WhoAmI()`, { headers });
    if (!whoAmIRes.ok) {
        throw new Error(`WhoAmI() failed (${whoAmIRes.status}). Check that your Entra app is registered as an Application User in Dataverse with a security role. See docs/dataverse-setup.md §2–3.`);
    }
    const who = (await whoAmIRes.json());
    const brRes = await fetch(`${dataverseUrl}/api/data/v9.2/bookableresources?$filter=_userid_value eq ${who.UserId}&$select=bookableresourceid&$top=1`, { headers });
    if (!brRes.ok) {
        throw new Error(`Looking up your bookable resource failed (${brRes.status}).`);
    }
    const brBody = (await brRes.json());
    const br = brBody.value[0];
    if (!br) {
        throw new Error("Your Dataverse user has no bookable resource record. A bookable resource is required to log time. Ask your admin to create one for you (Resource type = User, User = your account).");
    }
    return {
        systemUserId: who.UserId,
        bookableResourceId: br.bookableresourceid,
        businessUnitId: who.BusinessUnitId,
    };
}
function accountToUser(account, identity) {
    const claims = (account.idTokenClaims ?? {});
    return {
        id: identity.systemUserId,
        bookableResourceId: identity.bookableResourceId,
        name: account.name ?? claims.name ?? account.username,
        email: account.username,
        jobTitle: claims.jobTitle,
        businessUnitId: identity.businessUnitId,
    };
}
function rolesFromAccount(account) {
    const claims = (account.idTokenClaims ?? {});
    const raw = claims.roles;
    if (!Array.isArray(raw))
        return [];
    return raw.filter((r) => VALID_ROLES.includes(r));
}
export function useAuth() {
    const v = useContext(Ctx);
    if (!v)
        throw new Error("useAuth must be used inside <AuthProvider>");
    return v;
}
export function useRoles() {
    return useAuth().roles;
}
export function hasRole(roles, r) {
    return roles.includes(r);
}
